import { can } from "modules/settings/accessRoles";
import React, { useEffect } from "react";
import styled, { withTheme } from "styled-components";
import { spacing } from "@mui/system";
import {
  Box,
  Button as MuiButton,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TableContainer from "@mui/material/TableContainer";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import Skeleton from "@mui/material/Skeleton";
import EditIcon from "@mui/icons-material/Edit";
import style from "./styling/style";
import PropertyForm from "./Form";
import SitePlanUpload from "./SitePlanUpload";
import { Description, Upload } from "@mui/icons-material";
import { get as getPropertyStatuses } from "modules/propertyStatus/api/actions";

const Divider = styled(MuiDivider)(spacing);
const Button = styled(MuiButton)(spacing);

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function DetailCard(props) {
  const useStyles = style;
  const classes = useStyles();
  const dispatch = useDispatch();

  const [display, setDisplay] = React.useState(false);
  const [uploadDisplay, setUploadDisplay] = React.useState(false);
  const [hasExistingSitePlan, setHasExistingSitePlan] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const canEdit = can(props.user.details, "editRecords");

  const toggleDisplay = () => {
    const shouldDisplay = !display;
    setDisplay(shouldDisplay);
  };

  const toggleUploadDisplay = () => {
    setUploadDisplay(!uploadDisplay);
  };

  const onClose = () => {
    setUploadDisplay(false);
  };

  useEffect(() => {
    setHasExistingSitePlan(Boolean(props.properties.data.property.sitePlans))
  },[props.properties.data.property])

  useEffect(() => {
    dispatch(getPropertyStatuses());
  }, [dispatch]);

  return (
    <Card mb={6}>
      <CardContent xs={12}>
        <Grid container>
          <Grid item xs={6} sm={6} md={6} lg={6}>
            <Typography variant="h6" gutterBottom>
              Property Info
            </Typography>
          </Grid>
          <Grid item xs={3} sm={3} md={3} lg={3}>
            <IconButton 
              onClick={() => setExpanded(!expanded)}
              size="small"
              sx={{ 
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' }
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Grid>
          <Grid
            item
            xs={3}
            sm={3}
            md={3}
            lg={3}
            className={classes.rightSideHeaderSubMenuToolBar}
          >
            {canEdit && (
            <Button onClick={toggleDisplay}>
              <EditIcon 
                fontSize="medium"
                className={classes.SubMenuToolBarIcon}
              />
            </Button>
            )}
            <Dialog
              open={display}
              TransitionComponent={Transition}
              fullWidth={"lg"}
              maxWidth={"lg"}
              keepMounted
              onClose={toggleDisplay}
              aria-labelledby="alert-dialog-slide-title"
              aria-describedby="alert-dialog-slide-description"
            >
              {props.properties.data.property ? (
                <DialogTitle id="alert-dialog-slide-title">
                  Property: {props.properties.data.property.name}{" "}
                </DialogTitle>
              ) : (
                <DialogTitle id="alert-dialog-slide-title">
                  Loading Property.....{" "}
                </DialogTitle>
              )}
              <DialogContent>
                <PropertyForm
                  key={props.properties.data.property?.id || 'new'}
                  onUpdate={toggleDisplay}
                  onCancel={toggleDisplay}
                />
              </DialogContent>
            </Dialog>
          </Grid>
        </Grid>
        <Divider className="mb-2 mt-2" my={1} />
        {props.properties.propertyIsLoading ? (
          <>
            {[...Array(4)].map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                height="40px"
                sx={{
                  bgcolor: 'action.hover',
                  m: 0.75,
                  mb: 0.375
                }}
              />
            ))}
          </>
        ) : (
          <Grid
            container
            spacing={2}
            className={classes.PropertyInfoGrid}
          >
            <Grid item xs={12} lg={12} spacing={2}>
              <Box sx={{ p: 2 }}>
                {/* Two Column Grid Layout */}
                <Grid container spacing={3}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    {/* Property Name */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em'
                      }}>
                        PROPERTY NAME
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500, 
                        mt: 0.5,
                        color: 'text.primary'
                      }}>
                        {props.properties.data.property.name || "N/A"}
                      </Typography>
                    </Box>

                    {/* Address */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em'
                      }}>
                        ADDRESS
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 400, 
                        mt: 0.5,
                        color: 'text.primary',
                        lineHeight: 1.4
                      }}>
                        {props.properties.data.property.address || "Address not provided"}
                        <br />
                        {`${props.properties.data.property.city}, ${props.properties.data.property.state} ${props.properties.data.property.zip}`}
                      </Typography>
                    </Box>

                    {/* Developer */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em'
                      }}>
                        DEVELOPER
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500, 
                        mt: 0.5,
                        color: 'text.primary'
                      }}>
                        {props.properties.data.property.developer
                          ? props.properties.data.property.developer.name
                          : "N/A"}
                      </Typography>
                    </Box>

                    {/* Settings Row */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        mb: 1,
                        display: 'block'
                      }}>
                        PROPERTY SETTINGS
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={props.properties.data.property.isRemote == 1}
                            disabled={true}
                            size="small"
                          />
                          <Typography variant="body2" color="text.primary">Remote Property</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={props.properties.data.property.pmTenantFinal == 1}
                            disabled={true}
                            size="small"
                          />
                          <Typography variant="body2" color="text.primary">PM Owes Tenant Finals</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={props.properties.data.property.pmMax == 1}
                            disabled={true}
                            size="small"
                          />
                          <Typography variant="body2" color="text.primary">$300.00 PM Max</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    {/* Owner */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em'
                      }}>
                        OWNER
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500, 
                        mt: 0.5,
                        color: 'text.primary'
                      }}>
                        {props.properties.data.property.owner
                          ? props.properties.data.property.owner.name
                          : "N/A"}
                      </Typography>
                    </Box>

                    {/* Status */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em'
                      }}>
                        STATUS
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500, 
                        mt: 0.5,
                        color: 'text.primary'
                      }}>
                        {props.properties.data.property.status
                          ? props.properties.data.property.status.description
                          : "N/A"}
                      </Typography>
                    </Box>

                    {/* Last Updated */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="overline" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em'
                      }}>
                        LAST UPDATED
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500, 
                        mt: 0.5,
                        color: 'text.primary'
                      }}>
                        {new Date(
                          props.properties.data.property.updated
                        ).toLocaleDateString() || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                {/* Site Map Section */}
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, mt: 2 }}>
                  <Typography variant="overline" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    mb: 1.5,
                    display: 'block'
                  }}>
                    SITE MAP
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {hasExistingSitePlan && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Description fontSize="small" />}
                        onClick={() => window.open(props.properties.data.property.sitePlanSignedUrl, '_blank')}
                        sx={{ textTransform: 'none' }}
                      >
                        View
                      </Button>
                    )}
                    {canEdit && (
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      startIcon={<Upload />}
                      onClick={toggleUploadDisplay}
                      sx={{ textTransform: 'none' }}
                    >
                      Upload
                    </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Dialog
              open={uploadDisplay}
              onClose={toggleUploadDisplay}>
              <DialogTitle id="overwrite-dialog-title">
                Upload Site Plan
              </DialogTitle>
              <DialogContent>
                <SitePlanUpload
                  property={props.properties.data.property}
                  hasExistingSitePlan={hasExistingSitePlan}
                  isLoading={props.properties.propertyIsLoading}
                  onClose={onClose}
                />
              </DialogContent>
            </Dialog>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

DetailCard.propTypes = {
  user: PropTypes.object.isRequired,
  apl: PropTypes.object.isRequired,
  properties: PropTypes.object.isRequired,
};

function DetailCardState(state) {
  return {
    user: state.user,
    apl: state.apl,
    properties: state.properties,
  };
}

export default connect(DetailCardState, {})(DetailCard);
